import React, { useEffect, useState } from 'react';
import { logout, I18n } from '@kineticdata/react';
import { LoginWrapper } from './LoginWrapper';
import {
  Modal,
  ModalBody,
  ModalFooter,
  UncontrolledButtonDropdown,
  DropdownMenu,
  DropdownToggle,
  DropdownItem,
} from 'reactstrap';
import { Helmet } from 'react-helmet';

export const Login = ({
  loginProps,
  title = 'Sign In',
  subtitle,
  isLogout,
}) => {
  return (
    <LoginWrapper title={title} subtitle={subtitle}>
      <Helmet>
        <title>{title} | USDA</title>
      </Helmet>
      <LoginForm {...loginProps} auto={!isLogout}>
        {({ fields, buttons, handlers }) => (
          <>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {fields.username}
              {fields.password}

              {loginProps.error && (
                <span className="text-danger mb-3">
                  <I18n>{loginProps.error}</I18n>
                </span>
              )}
            </div>
            <div className="btn-group">
              {buttons.login}
              {buttons.ssoLogin}
              <UncontrolledButtonDropdown>
                <DropdownToggle caret title="Additional Options">
                  <span className="fa fa-angle-down" />
                </DropdownToggle>
                <DropdownMenu right>
                  {handlers.handleShowLocalLogin && (
                    <DropdownItem onClick={handlers.handleShowLocalLogin}>
                      <I18n>Show Local Login</I18n>
                    </DropdownItem>
                  )}
                  <DropdownItem onClick={() => window.open('/app', '_blank')}>
                    <I18n>Open Kinetic Console</I18n>
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledButtonDropdown>
            </div>
          </>
        )}
      </LoginForm>
    </LoginWrapper>
  );
};

export const LoginModal = ({ loginProps }) => (
  <Modal isOpen toggle={logout} size="lg" backdrop="static">
    <LoginForm {...loginProps} auto={false}>
      {({ fields, buttons, handlers }) => (
        <>
          <div className="modal-header">
            <h4 className="modal-title">
              <button type="button" className="btn btn-link" onClick={logout}>
                <I18n>Cancel</I18n>
              </button>
              <span>
                <I18n>Session Timed Out</I18n>
              </span>
              {handlers.handleShowLocalLogin && (
                <button
                  type="button"
                  className="btn btn-link"
                  onClick={handlers.handleShowLocalLogin}
                >
                  <I18n>Show Local Login</I18n>
                </button>
              )}
            </h4>
          </div>
          <ModalBody>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div className="form-group">
                <I18n>
                  Your session has timed out. Please reauthenticate to continue.
                </I18n>
              </div>
              {fields.username}
              {fields.password}

              <span className="text-danger">
                <I18n>{loginProps.error || ' '}</I18n>
              </span>
            </div>
          </ModalBody>
          <ModalFooter>
            {buttons.login}
            {buttons.ssoLogin}
          </ModalFooter>
        </>
      )}
    </LoginForm>
  </Modal>
);

export const LoginForm = ({
  error,
  username,
  onChangeUsername,
  password,
  onChangePassword,
  onLogin,
  onSso,
  pending,
  auto = true,
  children,
}) => {
  const ssoEnabled = typeof onSso === 'function';
  const isLocalDev = process.env.NODE_ENV === 'development';
  const [localLogin, setLocalLogin] = useState(!ssoEnabled || isLocalDev);
  const handleShowLocalLogin = () => setLocalLogin(true);

  useEffect(
    () => {
      if (ssoEnabled && !isLocalDev && auto) {
        onSso();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ssoEnabled, isLocalDev, auto],
  );

  const fields = {
    username: localLogin ? (
      <div className="form-group">
        <label htmlFor="username">
          <I18n>Email Address</I18n>
        </label>
        <input
          type="text"
          className="form-control"
          id="username"
          placeholder="wally@kineticdata.com"
          value={username}
          onChange={onChangeUsername}
        />
      </div>
    ) : (
      undefined
    ),
    password: localLogin ? (
      <div className="form-group">
        <label htmlFor="password">
          <I18n>Password</I18n>
        </label>
        <input
          type="password"
          className="form-control"
          id="password"
          placeholder="password"
          value={password}
          onChange={onChangePassword}
        />
      </div>
    ) : (
      undefined
    ),
  };
  const buttons = {
    login: localLogin ? (
      <button
        className="btn btn-primary"
        type="submit"
        disabled={pending || !username || !password}
      >
        <I18n>Sign In</I18n>
      </button>
    ) : (
      undefined
    ),
    ssoLogin: ssoEnabled ? (
      <button
        className="btn btn-secondary"
        type="button"
        onClick={onSso}
        disabled={pending}
      >
        <I18n>Enterprise Sign In</I18n>
      </button>
    ) : (
      undefined
    ),
  };
  const handlers = {
    handleShowLocalLogin: !localLogin ? handleShowLocalLogin : undefined,
  };
  return (
    <form className="login-form-container" onSubmit={onLogin}>
      {typeof children === 'function' ? (
        children({ fields, buttons, handlers })
      ) : (
        <>
          <div>{Object.values(fields)}</div>
          <div>{Object.values(buttons)}</div>
        </>
      )}
    </form>
  );
};
